import React, { useEffect, useRef, useState } from 'react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Fab, Tooltip } from '@mui/material';

// LedgerX Customer Support WhatsApp number (E.164, digits only) and prefilled greeting.
const WHATSAPP_SUPPORT_NUMBER = '916282000287';
const WHATSAPP_SUPPORT_MESSAGE = 'Hello LedgerX Support, I need assistance.';

// Matches the AI floating widget's launcher Fab (src/features/ChatBot/FloatingChatWidget.tsx),
// so the two floating buttons in the app are visually consistent in size.
export const FAB_SIZE = 40;
export const EDGE_MARGIN = 16;
// The button's bottom edge never comes closer than this to the bottom of the screen, on any
// screen size. Bottom nav bars in this app top out at 70px tall, so keep this above 70 - bump it
// here if a taller nav bar variant is ever added.
export const BOTTOM_CLEARANCE = 80;
const DRAG_THRESHOLD = 5;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getAvailableSpace = () => ({
  width: Math.max(1, window.innerWidth - FAB_SIZE - 2 * EDGE_MARGIN),
  height: Math.max(1, window.innerHeight - FAB_SIZE - EDGE_MARGIN - BOTTOM_CLEARANCE),
});

// Position is stored as a 0-1 fraction of the draggable area (not raw pixels) so that when the
// viewport is resized, the button stays anchored to the same *relative* spot instead of a stale
// pixel coordinate - shrink the window and it moves in proportionally; grow it back and it returns.
export const ratioToPosition = (ratio: { x: number; y: number }) => {
  const { width, height } = getAvailableSpace();
  return {
    x: EDGE_MARGIN + clamp(ratio.x, 0, 1) * width,
    y: EDGE_MARGIN + clamp(ratio.y, 0, 1) * height,
  };
};

export const positionToRatio = (pos: { x: number; y: number }) => {
  const { width, height } = getAvailableSpace();
  return {
    x: clamp((pos.x - EDGE_MARGIN) / width, 0, 1),
    y: clamp((pos.y - EDGE_MARGIN) / height, 0, 1),
  };
};

// After the user lets go, the button settles against whichever side of the screen it's closer to
// (left or right) rather than resting wherever it happened to be dropped horizontally.
export const snapRatioToNearestSide = (ratio: { x: number; y: number }) => ({
  x: ratio.x < 0.5 ? 0 : 1,
  y: ratio.y,
});

// Bottom-right corner of the draggable area, which already stops BOTTOM_CLEARANCE short of the
// bottom edge.
export const getDefaultRatio = () => ({ x: 1, y: 1 });

export const getWhatsAppUrl = () =>
  `https://wa.me/${WHATSAPP_SUPPORT_NUMBER}?text=${encodeURIComponent(WHATSAPP_SUPPORT_MESSAGE)}`;

export const WhatsAppSupportButton = () => {
  const [ratio, setRatio] = useState(getDefaultRatio);
  const [isDragging, setIsDragging] = useState(false);
  const [, triggerRelayout] = useState(0);
  const draggingRef = useRef(false);
  const movedRef = useRef(false);
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const dragOffsetRef = useRef({ x: 0, y: 0 });

  // The viewport can change size (resize, orientation change) at any time; re-render so the pixel
  // position is recomputed from the stored ratio, which is what keeps it "in the same spot".
  useEffect(() => {
    const handleResize = () => triggerRelayout((n) => n + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const position = ratioToPosition(ratio);

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    draggingRef.current = true;
    movedRef.current = false;
    setIsDragging(true);
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    dragOffsetRef.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!draggingRef.current) return;
    if (
      !movedRef.current &&
      (Math.abs(e.clientX - pointerStartRef.current.x) > DRAG_THRESHOLD ||
        Math.abs(e.clientY - pointerStartRef.current.y) > DRAG_THRESHOLD)
    ) {
      movedRef.current = true;
    }
    setRatio(
      positionToRatio({
        x: e.clientX - dragOffsetRef.current.x,
        y: e.clientY - dragOffsetRef.current.y,
      })
    );
  };

  const endDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    draggingRef.current = false;
    setIsDragging(false);
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (movedRef.current) {
      setRatio((prev) => snapRatioToNearestSide(prev));
    }
  };

  const handleClick = () => {
    if (movedRef.current) return; // it was a drag, not a tap/click
    window.open(getWhatsAppUrl(), '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip title="Chat with us on WhatsApp" placement="left" arrow>
      <Fab
        aria-label="WhatsApp support"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClick={handleClick}
        style={{
          left: position.x,
          top: position.y,
          transition: isDragging ? 'none' : 'left 200ms ease, top 200ms ease',
        }}
        sx={{
          position: 'fixed',
          touchAction: 'none',
          zIndex: 1250,
          width: FAB_SIZE,
          height: FAB_SIZE,
          minHeight: FAB_SIZE,
          padding: 0,
          backgroundColor: '#25D366',
          color: '#fff',
          cursor: 'grab',
          '&:active': {
            cursor: 'grabbing',
          },
          '&:hover': {
            backgroundColor: '#1DA851',
          },
        }}
      >
        <WhatsAppIcon sx={{ fontSize: FAB_SIZE * 0.8 }} />
      </Fab>
    </Tooltip>
  );
};

export default WhatsAppSupportButton;
